import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Table } from '@bb-ui/react-library/dist/components/Table';
import { TableBody } from '@bb-ui/react-library/dist/components/TableBody';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import {
    Button,
    DefaultButton,
} from '@bb-ui/react-library/dist/components/Button';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { CircularProgress } from '@bb-ui/react-library/dist/components/Progress';
import { ButtonBase } from '@bb-ui/react-library/dist/components/ButtonBase';
import { Collapse } from '@bb-ui/react-library/dist/components/Collapse';
import { ChevronDown } from '@bb-ui/icons/dist/small/ChevronDown';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import OffScreenAnnouncement from '@bb-ui/react-library/dist/components/OffScreenAnnouncement/OffScreenAnnouncement';
import { useAuditLog } from '../hooks/useAuditLog';
import { useStyles } from '../Authentication.styles';
import { useI18nContext } from '../../../I18nProvider/I18nProvider';
import { getFormatLocale } from '../../../../utilities/localization';
import {
    ACTION_TYPE_PREFIX,
    detailPairs,
    formatOccurredAt,
} from './auditEntryText';
import { AuditDetailsValue } from './AuditDetailsValue';
import { ScreenReaderOnly } from '../../../ScreenReaderOnly/ScreenReaderOnly';
import { SkeletonTable } from '../../../Skeleton/SkeletonTable';

/**
 * Read-only view of authentication configuration changes, in a collapsible panel.
 * Expanded by default, and collapsible so it can be folded away once read.
 */
interface AuditLogProps {
    /** Changes when a change has been written, so the log is re-read. */
    reloadToken?: number;
}

export const AuditLog: React.FunctionComponent<AuditLogProps> = ({
    reloadToken = 0,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { userSelectedLang } = useI18nContext();
    const locale = getFormatLocale(userSelectedLang);

    // An unknown details key falls back to the key itself rather than an empty
    // cell -- the service may add keys without a contract change.
    const detailLabel = React.useCallback(
        (key: string) =>
            t(key, { defaultValue: key.split('.').pop() as string }),
        [t],
    );
    // An action the UI doesn't know yet is shown as sent, never dropped.
    const actionLabel = React.useCallback(
        (actionType: string) =>
            t(`${ACTION_TYPE_PREFIX}.${actionType}`, {
                defaultValue: actionType,
            }),
        [t],
    );
    const {
        entries,
        loadFirstPage,
        loadMore,
        hasMore,
        isEmpty,
        isLoading,
        loadFailed,
        isLoadingMore,
        loadMoreFailed,
    } = useAuditLog();

    const [expanded, setExpanded] = React.useState(true);

    const tableRef = React.useRef<HTMLTableElement>(null);
    const resultsRef = React.useRef<HTMLDivElement>(null);
    const awaitingLastPage = React.useRef(false);
    const firstNewRow = React.useRef(0);
    const [announcement, setAnnouncement] = React.useState('');
    const announceNextPage = React.useRef(false);
    const entryCount = entries.length;

    const handleLoadMore = React.useCallback(() => {
        if (!loadMore()) return;
        awaitingLastPage.current = true;
        announceNextPage.current = true;
        firstNewRow.current = entryCount;
        setAnnouncement(t('settings.authentication.audit.loadingMore'));
    }, [entryCount, loadMore, t]);

    // A Show more that failed or was superseded must not announce, or move
    // focus, when a later read changes the rows.
    const forgetLoadMore = React.useCallback(() => {
        awaitingLastPage.current = false;
        announceNextPage.current = false;
    }, []);

    React.useEffect(() => {
        if (loadMoreFailed) forgetLoadMore();
    }, [loadMoreFailed, forgetLoadMore]);

    const reload = React.useCallback(() => {
        forgetLoadMore();
        loadFirstPage();
    }, [forgetLoadMore, loadFirstPage]);

    const handleRetry = React.useCallback(() => {
        (entryCount > 0 ? tableRef : resultsRef).current?.focus();
        reload();
    }, [entryCount, reload]);

    // Keyed on hasMore as well as the count, so a final page that adds no rows
    // still says so rather than leaving the request unacknowledged.
    React.useEffect(() => {
        if (!announceNextPage.current) return;
        announceNextPage.current = false;
        setAnnouncement(
            t('settings.authentication.audit.moreLoaded', {
                total: entryCount,
            }),
        );
    }, [entryCount, hasMore, t]);

    React.useEffect(() => {
        if (!awaitingLastPage.current || hasMore) return;
        awaitingLastPage.current = false;
        // The button is gone: focus the first new row, or the last row if the
        // final page added none
        const rows = Array.from(
            tableRef.current?.querySelectorAll<HTMLElement>('tbody tr') ?? [],
        );
        const row = rows[Math.min(firstNewRow.current, rows.length - 1)];
        row?.setAttribute('tabindex', '-1');
        row?.focus();
    }, [hasMore]);

    /**
     * Loaded once per token: re-expanding re-reads nothing, but a change
     * written elsewhere on the tab moves the token and the log catches up.
     */
    const loadedToken = React.useRef<number>();
    React.useEffect(() => {
        if (!expanded || loadedToken.current === reloadToken) return;
        loadedToken.current = reloadToken;
        reload();
    }, [expanded, reload, reloadToken]);

    // APG accordion: the heading wraps a native button, so it stays a heading
    // and the toggle gets its keyboard handling from the browser.
    return (
        <section className={classes.auditPanel} data-testid="auditPanel">
            <Typography
                variant="h3"
                component="h2"
                id="authentication-audit-summary"
                className={classes.auditHeading}
            >
                <ButtonBase
                    className={classes.auditToggle}
                    aria-expanded={expanded}
                    aria-controls="authentication-audit-details"
                    onClick={() => setExpanded(isExpanded => !isExpanded)}
                    data-testid="auditPanelToggle"
                >
                    {t('settings.authentication.audit.heading')}
                    <ChevronDown
                        role="presentation"
                        className={classnames(classes.auditChevron, {
                            [classes.auditChevronOpen]: expanded,
                        })}
                    />
                </ButtonBase>
            </Typography>
            {/* Collapse takes no id, so the element it controls wraps it. */}
            <div id="authentication-audit-details">
                <Collapse
                    in={expanded}
                    classes={{ wrapperInner: classes.auditDetails }}
                >
                    <ScreenReaderOnly
                        type="p"
                        aria-live="polite"
                        role="status"
                        data-testid="auditAnnouncement"
                    >
                        {announcement}
                    </ScreenReaderOnly>
                    <div
                        ref={resultsRef}
                        tabIndex={-1}
                        className={classes.auditFullWidth}
                        data-testid="auditResults"
                    >
                        {isLoading && (
                            <div
                                className={classes.auditFullWidth}
                                data-testid="auditLoading"
                            >
                                <OffScreenAnnouncement
                                    type="polite"
                                    message={t(
                                        'settings.authentication.audit.loading',
                                    )}
                                    changeTrigger={1}
                                />
                                <SkeletonTable columnNum={5} rowNum={5} />
                            </div>
                        )}
                        {loadFailed && (
                            <Banner
                                severity="error"
                                className={classnames(
                                    classes.banner,
                                    classes.auditFullWidth,
                                )}
                                data-testid="auditLoadError"
                            >
                                {t('settings.authentication.audit.loadError')}
                                <DefaultButton
                                    className={classes.bannerAction}
                                    onClick={handleRetry}
                                    data-testid="auditRetry"
                                >
                                    {t('settings.authentication.list.retry')}
                                </DefaultButton>
                            </Banner>
                        )}
                        {isEmpty && (
                            <p
                                className={classes.emptyState}
                                data-testid="auditEmpty"
                            >
                                {t('settings.authentication.audit.empty')}
                            </p>
                        )}
                    </div>
                    {entries.length > 0 && (
                        <>
                            {/* The tab stop is on the table, not a region around
                            it: the heading already names the activity. */}
                            <div className={classes.auditTableContainer}>
                                <Table
                                    ref={tableRef}
                                    className={classes.auditTable}
                                    tabIndex={0}
                                    aria-labelledby="authentication-audit-summary"
                                    data-testid="auditTable"
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                className={
                                                    classes.timestampHeaderCell
                                                }
                                            >
                                                {t(
                                                    'settings.authentication.audit.colTimestamp',
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className={classes.actionCell}
                                            >
                                                {t(
                                                    'settings.authentication.audit.colAction',
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className={
                                                    classes.performedByCell
                                                }
                                            >
                                                {t(
                                                    'settings.authentication.audit.colPerformedBy',
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className={classes.providerCell}
                                            >
                                                {t(
                                                    'settings.authentication.audit.colProvider',
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className={classes.detailsCell}
                                            >
                                                {t(
                                                    'settings.authentication.audit.colDetails',
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entries.map(e => {
                                            const pairs = detailPairs(
                                                e,
                                                detailLabel,
                                            );
                                            return (
                                                <TableRow
                                                    key={`${e.occurredAt}-${e.actorDisplay}`}
                                                >
                                                    <TableCell
                                                        className={
                                                            classes.timestampCell
                                                        }
                                                    >
                                                        <time
                                                            dateTime={new Date(
                                                                e.occurredAt *
                                                                    1000,
                                                            ).toISOString()}
                                                        >
                                                            {formatOccurredAt(
                                                                e.occurredAt,
                                                                locale,
                                                            )}
                                                        </time>
                                                    </TableCell>
                                                    <TableCell
                                                        className={
                                                            classes.actionCell
                                                        }
                                                    >
                                                        {actionLabel(
                                                            e.actionType,
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        className={
                                                            classes.performedByCell
                                                        }
                                                    >
                                                        {e.actorDisplay}
                                                    </TableCell>
                                                    <TableCell
                                                        className={
                                                            classes.providerCell
                                                        }
                                                    >
                                                        {e.idpDisplayName ?? ''}
                                                    </TableCell>
                                                    <TableCell
                                                        className={
                                                            classes.detailsCell
                                                        }
                                                    >
                                                        {pairs.length > 0 && (
                                                            <AuditDetailsValue
                                                                pairs={pairs}
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                            {hasMore && (
                                <Button
                                    variant="outlined"
                                    className={classes.loadMore}
                                    onClick={handleLoadMore}
                                    // Not `disabled`: that would drop keyboard focus
                                    // to <body> mid-load.
                                    aria-disabled={isLoadingMore}
                                    aria-busy={isLoadingMore}
                                    data-testid="auditLoadMore"
                                >
                                    {isLoadingMore && (
                                        // Announced through the panel's live
                                        // region, not as part of the button name.
                                        <span aria-hidden="true">
                                            <CircularProgress
                                                size={14}
                                                className={
                                                    classes.loadMoreSpinner
                                                }
                                                ariaLabel={t(
                                                    'settings.authentication.audit.loadingMore',
                                                )}
                                            />
                                        </span>
                                    )}
                                    {t('general.showMore')}
                                </Button>
                            )}
                            {loadMoreFailed && (
                                <Banner
                                    severity="error"
                                    className={classnames(
                                        classes.banner,
                                        classes.auditFullWidth,
                                    )}
                                    data-testid="auditLoadMoreError"
                                >
                                    {t(
                                        'settings.authentication.audit.loadMoreError',
                                    )}
                                </Banner>
                            )}
                        </>
                    )}
                </Collapse>
            </div>
        </section>
    );
};
